import { DateTime, Settings } from 'luxon';
import env from '@trashlab/viteapp/src/env';
import type { ScenarioControls } from './scenario-lab.types';

const MAX_COMPONENTS = 50;
const MAX_FLAGS = 50;
const MAX_KEY_LENGTH = 100;
const MAX_VALUE_LENGTH = 100;

const nativeNow = Settings.now;
const nativeLocale = Settings.defaultLocale;
const nativeZone = Settings.defaultZone;
const nativeDateParse = Date.parse;
const listeners = new Set<() => void>();

export const DEFAULT_SCENARIO_CONTROLS: ScenarioControls = {
  components: {},
  event: null,
  flags: {},
  locale: null,
  role: null,
  time: null,
  timeZone: null,
  viewport: null,
};

let currentControls = DEFAULT_SCENARIO_CONTROLS;

function recordOfStrings(value: unknown, limit: number) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter((entry): entry is [string, string] => {
        const [key, child] = entry;
        return (
          key.length > 0 &&
          key.length <= MAX_KEY_LENGTH &&
          typeof child === 'string' &&
          child.length > 0 &&
          child.length <= MAX_VALUE_LENGTH
        );
      })
      .slice(0, limit),
  );
}

function recordOfBooleans(value: unknown, limit: number) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(
        ([key, child]) =>
          key.length > 0 &&
          key.length <= MAX_KEY_LENGTH &&
          typeof child === 'boolean',
      )
      .slice(0, limit),
  ) as Record<string, boolean>;
}

function validLocale(value: unknown) {
  if (typeof value !== 'string' || value.length === 0 || value.length > 100)
    return null;
  try {
    return Intl.getCanonicalLocales(value)[0] ?? null;
  } catch {
    return null;
  }
}

function validTime(value: unknown) {
  if (
    typeof value !== 'string' ||
    value.length > 100 ||
    !/^\d{4}-\d{2}-\d{2}T/.test(value)
  ) {
    return null;
  }
  const millis = nativeDateParse(value);
  return Number.isFinite(millis) ? new Date(millis).toISOString() : null;
}

function validTimeZone(value: unknown) {
  if (typeof value !== 'string' || value.length === 0 || value.length > 100)
    return null;
  return DateTime.fromMillis(nativeNow()).setZone(value).isValid ? value : null;
}

export function normalizeScenarioControls(
  value: Partial<ScenarioControls> | null | undefined,
): ScenarioControls {
  return {
    components: recordOfStrings(value?.components, MAX_COMPONENTS),
    event:
      value?.event === 'inbound-call' ||
      value?.event === 'driver-completed-stop' ||
      value?.event === 'payment-landed'
        ? value.event
        : null,
    flags: recordOfBooleans(value?.flags, MAX_FLAGS),
    locale: validLocale(value?.locale),
    role:
      value?.role === 'OWNER' ||
      value?.role === 'DISPATCHER_LIMITED' ||
      value?.role === 'DRIVER' ||
      value?.role === 'SALES_REP_LIMITED'
        ? value.role
        : null,
    time: validTime(value?.time),
    timeZone: validTimeZone(value?.timeZone),
    viewport:
      typeof value?.viewport === 'number' &&
      Number.isInteger(value.viewport) &&
      value.viewport >= 320 &&
      value.viewport <= 3_840
        ? value.viewport
        : null,
  };
}

export function scenarioControlsActive(controls: ScenarioControls) {
  return (
    controls.time !== null ||
    controls.locale !== null ||
    controls.timeZone !== null ||
    controls.viewport !== null ||
    controls.role !== null ||
    controls.event !== null ||
    Object.keys(controls.flags).length > 0 ||
    Object.keys(controls.components).length > 0
  );
}

export function applyScenarioDisplayControls(controls: ScenarioControls) {
  // Parse before installing Settings.now: Luxon's DateTime.fromISO may call
  // Settings.now internally, which would recursively invoke this clock.
  const frozenMillis = controls.time ? nativeDateParse(controls.time) : null;
  Settings.now = Number.isFinite(frozenMillis)
    ? () => frozenMillis!
    : nativeNow;
  Settings.defaultLocale = controls.locale ?? nativeLocale;
  Settings.defaultZone = controls.timeZone ?? nativeZone;
}

export function getScenarioLabControls() {
  return currentControls;
}

export function subscribeScenarioLabControls(listener: () => void) {
  if (!isScenarioLabEnabled()) return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setScenarioLabControlsStore(controls: ScenarioControls) {
  if (scenarioControlsEqual(currentControls, controls)) return;
  currentControls = controls;
  listeners.forEach((listener) => listener());
}

export function scenarioLabFlagOverride(key: string) {
  if (!isScenarioLabEnabled()) return undefined;
  return currentControls.flags[key];
}

export function scenarioControlsEqual(
  left: ScenarioControls,
  right: ScenarioControls,
) {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** Controls are intentionally limited to development and PR previews. */
export function isScenarioLabEnabled() {
  return (
    env.NEXT_PUBLIC_SCENARIO_LAB === 'true' && Boolean(import.meta.env.DEV)
  );
}

/** @deprecated Use isScenarioLabEnabled. */
export const isScenarioLabControlsEnabled = isScenarioLabEnabled;
